/**
 * Environment configuration data
 * Extracted from legacy choco-admin-portal system
 *
 * Validation policy, so this file does not drift again:
 *
 * - An environment belongs here only if `choco-backend` deploys to a branch of
 *   the same name. Two entries here were never real, and their configs were
 *   copies of another environment's ids.
 * - Its ids must resolve. Probe the user pool with
 *   `https://cognito-idp.<region>.amazonaws.com/<userPoolId>/.well-known/jwks.json`
 *   (a 404 means the pool is gone) and resolve the AppSync host.
 * - An environment that is real but whose ids no longer resolve gets fresh ids,
 *   read from its own AWS account. Do not remove it, and do not leave the stale
 *   ids in place: `fetchAuthSession()` throws on ids that no longer resolve,
 *   which strips the Authorization header from every GraphQL request and reads
 *   as a broken portal rather than a misconfigured environment.
 * - The ids live in that environment's SSM parameters
 *   (`<env>.AdminUserPoolId`, `<env>.AdminUserPoolClientId`), its single Cognito
 *   identity pool, and its `<env>-graphql-api` AppSync API.
 *
 * Every environment listed here was validated this way on 2026-07-29.
 */

import { PRODUCTION_ENV_CONFIG } from './productionEnv';
import { EnvironmentConfig, EnvironmentKey } from './types';

/**
 * The LaunchDarkly environment everything outside production reads.
 *
 * The legacy admin, `choco-web`, `choco-web-lite` and `frontend` all point at
 * this same client-side ID outside production, so it is committed here for the
 * same reason it is committed there: a client-side ID is public by design, and
 * ships in the browser bundle either way.
 */
const SHARED_LAUNCH_DARKLY_CLIENT_ID = '5e4569eb47c8f508323f23f7';

export const ENVIRONMENT_CONFIGS: Record<EnvironmentKey, EnvironmentConfig> = {
  // Main staging environment
  chostg: {
    emoji: '🥤',
    name: 'Staging',
    appSyncUrl:
      'https://i5ikxhkptvbktlr7agoht2mn4y.appsync-api.eu-west-1.amazonaws.com/graphql',
    userPoolId: 'eu-west-1_NxbzoMbLF',
    identityPoolId: 'eu-west-1:d717ca60-2a3e-4e1c-bbd0-77298b70ae05',
    userPoolWebClientId: '6etad5sggc8c6mnge85oo1kj9j',
    region: 'eu-west-1',
    oauthDomain: 'choco-admin-chostg.auth.eu-west-1.amazoncognito.com',
    launchDarklyClientId: SHARED_LAUNCH_DARKLY_CLIENT_ID,
    isProduction: false,
  },

  // Dusk environment
  chodusk: {
    emoji: '🌆',
    name: 'Dusk',
    appSyncUrl:
      'https://i4pholtytzen5ds3ex2wvaj4n4.appsync-api.eu-west-1.amazonaws.com/graphql',
    userPoolId: 'eu-west-1_Ba6PJKiTN',
    // No identity pool for the admin app — it authenticates to AppSync via
    // Cognito User Pool ID tokens. The previously configured pool no longer
    // exists, and leaving it set makes fetchAuthSession() throw on credential
    // resolution, which strips the Authorization header from GraphQL requests.
    identityPoolId: '',
    userPoolWebClientId: 'f53v9qjo68ji5hd28pela9r4t',
    region: 'eu-west-1',
    oauthDomain: 'choco-admin-chodusk.auth.eu-west-1.amazoncognito.com',
    launchDarklyClientId: SHARED_LAUNCH_DARKLY_CLIENT_ID,
    isProduction: false,
  },

  // Zoo environment. Every value below was re-read from the environment's own
  // AWS account (558456645904) on 2026-07-29; the previous ones pointed at a
  // torn-down pool and an AppSync host that no longer resolves.
  chocozoo: {
    emoji: '🦁',
    name: 'Zoo',
    appSyncUrl:
      'https://cq64vrf4bncjfbsu25ssn5agxq.appsync-api.eu-west-1.amazonaws.com/graphql',
    userPoolId: 'eu-west-1_WmyNZxxEz',
    identityPoolId: 'eu-west-1:1653f026-5d51-41f8-8178-543a63c4d579',
    userPoolWebClientId: '4rp1hslogo2m0s2v062vn85ign',
    region: 'eu-west-1',
    oauthDomain: 'choco-admin-chocozoo.auth.eu-west-1.amazoncognito.com',
    launchDarklyClientId: SHARED_LAUNCH_DARKLY_CLIENT_ID,
    isProduction: false,
  },

  // Autumn environment
  chocoautumn: {
    emoji: '🍁',
    name: 'Autumn',
    appSyncUrl:
      'https://e5v6fulgt5byxi4luvanf5rx3e.appsync-api.eu-west-1.amazonaws.com/graphql',
    userPoolId: 'eu-west-1_lRDdeQaxy',
    identityPoolId: 'eu-west-1:e3a14e2e-9e83-46dc-af32-7bdf84840946',
    userPoolWebClientId: '7ubi1uv1nq94s2hetep2r5eo0r',
    region: 'eu-west-1',
    oauthDomain: 'choco-admin-chocoautumn.auth.eu-west-1.amazoncognito.com',
    launchDarklyClientId: SHARED_LAUNCH_DARKLY_CLIENT_ID,
    isProduction: false,
  },

  // Cat environment
  chococat: {
    emoji: '🐱',
    name: 'Cat',
    appSyncUrl:
      'https://xk5gojavqzb5de6m6xkpaufeaq.appsync-api.eu-west-1.amazonaws.com/graphql',
    userPoolId: 'eu-west-1_9owhZdcrU',
    identityPoolId: 'eu-west-1:a6206532-c00a-41f4-b41b-aa1b7de8ab6f',
    userPoolWebClientId: '5ipeo4u8baipe4lq0m4vmjklro',
    region: 'eu-west-1',
    oauthDomain: 'choco-admin-chococat.auth.eu-west-1.amazoncognito.com',
    launchDarklyClientId: SHARED_LAUNCH_DARKLY_CLIENT_ID,
    isProduction: false,
  },

  // Breakup environment
  chobreakup: {
    emoji: '💔',
    name: 'Breakup',
    appSyncUrl:
      'https://b7mklinglvb3fewkyihrqt2usy.appsync-api.eu-west-1.amazonaws.com/graphql',
    userPoolId: 'eu-west-1_895JzigBu',
    identityPoolId: 'eu-west-1:91e130ac-35f8-4b5b-a845-ab0f7f5c942d',
    userPoolWebClientId: '6f0f2j0tkpj6bcndvpjml0ggbl',
    region: 'eu-west-1',
    oauthDomain: 'choco-admin-chobreakup.auth.eu-west-1.amazoncognito.com',
    launchDarklyClientId: SHARED_LAUNCH_DARKLY_CLIENT_ID,
    isProduction: false,
  },

  // Platform infrastructure environment
  plinfra: {
    emoji: '👾',
    name: 'Platform Infrastructure',
    appSyncUrl:
      'https://3cdmblyg7rgj7h37htm5wjw2vy.appsync-api.eu-west-1.amazonaws.com/graphql',
    userPoolId: 'eu-west-1_rTRlQqKgh',
    identityPoolId: 'eu-west-1:c97da390-4e09-48fb-9fd0-72c92133d17b',
    userPoolWebClientId: '2r83c8nr18iv95q4nuevs8em0n',
    region: 'eu-west-1',
    oauthDomain: 'choco-admin-plinfra.auth.eu-west-1.amazoncognito.com',
    launchDarklyClientId: SHARED_LAUNCH_DARKLY_CLIENT_ID,
    isProduction: false,
  },

  // Production environment. Its values are injected at build time, and live in
  // productionEnv.ts so this module stays free of the `import.meta` token.
  choprd: {
    emoji: '🚔',
    name: 'Production',
    appSyncUrl: PRODUCTION_ENV_CONFIG.appSyncUrl,
    userPoolId: PRODUCTION_ENV_CONFIG.userPoolId,
    identityPoolId: PRODUCTION_ENV_CONFIG.identityPoolId,
    userPoolWebClientId: PRODUCTION_ENV_CONFIG.userPoolWebClientId,
    launchDarklyClientId: PRODUCTION_ENV_CONFIG.launchDarklyClientId,
    region: 'eu-west-1',
    oauthDomain: 'choco-admin-choprd.auth.eu-west-1.amazoncognito.com',
    isProduction: true,
  },
};

// Default environment for development
export const DEFAULT_ENVIRONMENT: EnvironmentKey = 'chostg';

// Production environment key
export const PRODUCTION_ENVIRONMENT: EnvironmentKey = 'choprd';
